import React, { useCallback } from 'react';
import { List, Tag, Button, Typography } from 'antd';
import { WarningOutlined, CheckCircleTwoTone } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { workOrderApi } from '../../../services/api';
import { WorkOrder } from '../../../types';
import { usePanelData } from './usePanelData';
import PanelCard from './PanelCard';

const { Text } = Typography;

const money = (n?: number) =>
  `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const vehicleLabel = (wo: WorkOrder) =>
  wo.vehicle ? `${wo.vehicle.year} ${wo.vehicle.make} ${wo.vehicle.model}` : '';
const customerLabel = (wo: WorkOrder) =>
  wo.customer ? `${wo.customer.first_name} ${wo.customer.last_name}` : '';

const NeedsAttentionPanel: React.FC = () => {
  const navigate = useNavigate();
  const fetcher = useCallback(
    () => workOrderApi.getAll({ status: 'waiting_approval', limit: 5 }).then((r) => r.data),
    []
  );
  const { data, loading, error, reload } = usePanelData<WorkOrder[]>(fetcher);
  const items = data ?? [];

  return (
    <PanelCard
      title="Needs Attention"
      icon={<WarningOutlined style={{ color: '#ff4d4f' }} />}
      count={items.length}
      loading={loading}
      error={error}
      isEmpty={items.length === 0}
      emptyText={
        <span>
          <CheckCircleTwoTone twoToneColor="#52c41a" /> All clear — no approvals needed
        </span>
      }
      onRetry={reload}
      extra={
        items.length > 0 ? (
          <Button type="link" onClick={() => navigate('/owner/work-orders?status=waiting_approval')}>
            View all
          </Button>
        ) : null
      }
    >
      <List
        dataSource={items}
        renderItem={(wo) => (
          <List.Item
            style={{ cursor: 'pointer', minHeight: 48 }}
            onClick={() => navigate(`/owner/work-orders/${wo.id}`)}
          >
            <List.Item.Meta
              title={
                <span>
                  <Text strong>{wo.wo_number}</Text> <Text type="secondary">{customerLabel(wo)}</Text>
                </span>
              }
              description={
                <span>
                  {vehicleLabel(wo)} · <Tag color="red">Needs Approval</Tag> {money(wo.total_amount)}
                </span>
              }
            />
          </List.Item>
        )}
      />
    </PanelCard>
  );
};

export default NeedsAttentionPanel;
